import * as THREE from "three";
import type { ClayKind, HoleFacing } from "./clay.ts";
import { computeStats, type IndexedMesh, meshSdf, type Sdf } from "./geometry.ts";

export interface TargetDef {
  id: string;
  /** What is being made, as named on the delivery slip. */
  item: string;
  /** The customer's word on the slip when the shape feature is the piece's best point. */
  praise: string;
  /** Starting clay; it has the same number of holes as the target. */
  clay: ClayKind;
  /** The way the starting clay's holes face, as the target's do (toward the camera if not given). */
  clayHoles?: HoleFacing;
  /** Shape feature judged on top of the common criteria. */
  feature: "waist" | "hole" | "ears";
  /**
   * The way the letter's thumbnail looks at it, toward its centre, if not as the kneading
   * starts off (see main.ts's THUMB_VIEW): from high above for a face laid face up.
   */
  thumbView?: [number, number, number];
  /** Stroke count that still earns the full efficiency bonus. */
  par: number;
  /** Unscaled shape. It must lie inside [-bound, bound]³. */
  sdf: Sdf;
  bound: number;
}

// ---------- SDF building blocks ----------

const len2 = (a: number, b: number) => Math.sqrt(a * a + b * b);

function smin(a: number, b: number, k: number): number {
  const h = Math.max(k - Math.abs(a - b), 0) / k;
  return Math.min(a, b) - h * h * k * 0.25;
}

const sphere = (cx: number, cy: number, cz: number, r: number): Sdf => (x, y, z) =>
  Math.sqrt((x - cx) ** 2 + (y - cy) ** 2 + (z - cz) ** 2) - r;

/** Upright (y-axis) cylinder with rounded edges. */
const roundedCylinder = (
  cx: number,
  cy: number,
  cz: number,
  radius: number,
  halfHeight: number,
  round: number,
): Sdf =>
(x, y, z) => {
  const dx = len2(x - cx, z - cz) - radius + round;
  const dy = Math.abs(y - cy) - halfHeight + round;
  return Math.min(Math.max(dx, dy), 0) + len2(Math.max(dx, 0), Math.max(dy, 0)) - round;
};

/** Ring lying in the xy plane (its hole faces the camera). */
const torusZ =
  (cx: number, cy: number, cz: number, major: number, minor: number): Sdf => (x, y, z) =>
    len2(len2(x - cx, y - cy) - major, z - cz) - minor;

const smoothUnion = (k: number, ...parts: Sdf[]): Sdf => (x, y, z) =>
  parts.slice(1).reduce((d, part) => smin(d, part(x, y, z), k), parts[0](x, y, z));

const smoothSubtract = (k: number, a: Sdf, b: Sdf): Sdf => (x, y, z) =>
  -smin(-a(x, y, z), b(x, y, z), k);

const intersect = (a: Sdf, b: Sdf): Sdf => (x, y, z) => Math.max(a(x, y, z), b(x, y, z));

const union = (...parts: Sdf[]): Sdf => (x, y, z) => Math.min(...parts.map((p) => p(x, y, z)));

type Point = [number, number, number];

/** Rod from a to b with rounded ends. */
const capsule = (a: Point, b: Point, r: number): Sdf => (x, y, z) => {
  const ba = [b[0] - a[0], b[1] - a[1], b[2] - a[2]], pa = [x - a[0], y - a[1], z - a[2]];
  const len2ba = ba[0] ** 2 + ba[1] ** 2 + ba[2] ** 2;
  const h = Math.min(1, Math.max(0, (pa[0] * ba[0] + pa[1] * ba[1] + pa[2] * ba[2]) / len2ba));
  return Math.hypot(pa[0] - ba[0] * h, pa[1] - ba[1] * h, pa[2] - ba[2] * h) - r;
};

/** Upright rod from height y0 (radius r0) to y1 (radius r1), rounded at both ends. */
const roundCone = (y0: number, r0: number, y1: number, r1: number): Sdf => (x, y, z) => {
  const h = y1 - y0, b = (r0 - r1) / h, a = Math.sqrt(1 - b * b);
  const qx = len2(x, z), qy = y - y0, k = -b * qx + a * qy;
  if (k < 0) return len2(qx, qy) - r0;
  if (k > a * h) return len2(qx, qy - h) - r1;
  return a * qx + b * qy - r0;
};

/** Everything above the height `y0`. */
const above = (y0: number): Sdf => (_x, y) => y0 - y;

/**
 * `sdf` stretched `k` times along `axis` and narrowed across it to keep its volume. It stays
 * a fair enough distance for meshing and scoring at moderate `k`.
 */
export function stretched(sdf: Sdf, axis: number, k: number): Sdf {
  const across = Math.sqrt(k);
  return (x, y, z) => {
    const p = [x * across, y * across, z * across];
    p[axis] = [x, y, z][axis] / k;
    return sdf(p[0], p[1], p[2]);
  };
}

/** Gourd: a big ball under a small one; lifting the small one narrows the neck. */
export const gourd = (lift = 0): Sdf =>
  smoothUnion(0.22 - lift / 10, sphere(0, -0.3, 0, 1.0), sphere(0, 0.95 + lift, 0, 0.5));
export const snowman: Sdf = smoothUnion(0.12, sphere(0, -0.55, 0, 0.95), sphere(0, 0.74, 0, 0.62));
export const peanut: Sdf = smoothUnion(0.3, sphere(-0.62, 0, 0, 0.78), sphere(0.62, 0, 0, 0.78));

/** A plump clay cat head; `ears` raises two soft cones from its crown. */
export function catHead(ears = 0.4): Sdf {
  const head: Sdf = (x, y, z) => {
    const cheekY = y + 0.1 + 0.12 * (x / 0.76) ** 2;
    const height = cheekY > 0 ? 0.42 : 0.5;
    const width = 0.76 + 0.035 * Math.exp(-(((y + 0.18) / 0.18) ** 2));
    const face = (Math.abs(x / width) ** 2.6 + Math.abs(cheekY / height) ** 2.6) ** (2 / 2.6);
    return (Math.sqrt(face + (z / 0.58) ** 2) - 1) * 0.55;
  };
  if (ears <= 0) return head;
  // Broad roots sit inside the bun; both width and depth taper toward rounded tips.
  const ear = roundCone(-0.04, 0.31, 0.18 + ears, 0.085);
  const pair: Sdf = (x, y, z) => {
    const rise = Math.min(1, Math.max(0, (y + 0.04) / (0.22 + ears)));
    const center = 0.31 + 0.15 * rise;
    return ear((Math.abs(x) - center) / 1.05, y, z / 0.85) * 0.85;
  };
  return smoothUnion(0.16, head, pair);
}

/** Mug whose handle ring is `handle` across (to the middle of its tube). */
export function mug(handle = 0.5): Sdf {
  const radius = 0.75, halfHeight = 0.85, wall = 0.2, floor = 0.25;
  const body = roundedCylinder(0, 0, 0, radius, halfHeight, 0.12);
  // The cavity runs from the floor up past the rim, so the cup is open at the top.
  const cavityBottom = -halfHeight + floor, cavityTop = halfHeight + 1;
  const cavity = roundedCylinder(
    0,
    (cavityBottom + cavityTop) / 2,
    0,
    radius - wall,
    (cavityTop - cavityBottom) / 2,
    0.1,
  );
  // Half a ring on the right; the part inside the wall is cut off so it stays out of the cup.
  const ring = intersect(torusZ(radius, 0, 0, handle, 0.15), (x) => radius - 0.1 - x);
  return smoothUnion(0.08, smoothSubtract(0.05, body, cavity), ring);
}

/**
 * Low watering can: a squat pot with a handle arching over it (the one hole) and a spout
 * tipped a little upward, ending in a sprinkler rose. `handle` is the arch's radius.
 */
export function wateringCan(handle = 0.42): Sdf {
  const top = 0.16;
  const pot = roundedCylinder(0, -0.2, 0, 0.62, 0.36, 0.14);
  const arch = intersect(torusZ(0, top - 0.05, 0, handle, 0.11), above(top - 0.05));
  const spout = capsule([0.4, -0.3, 0], [1.0, 0.2, 0], 0.1);
  return smoothUnion(0.08, pot, arch, spout, sphere(1.04, 0.24, 0, 0.15));
}

/**
 * Hand bell for a shopping-street lottery: a flared bell, hollow underneath, on a handle
 * with a knob (no hole). The handle widens upward from its neck; `grip` is its radius.
 */
export function bell(grip = 0.13): Sdf {
  const body = intersect(
    smoothUnion(0.35, sphere(0, -0.15, 0, 0.62), roundedCylinder(0, -0.62, 0, 0.78, 0.1, 0.08)),
    above(-0.72),
  );
  const hollow = smoothSubtract(0.1, body, sphere(0, -0.95, 0, 0.62));
  const handle = roundCone(0.4, grip * 0.75, 1.0, grip * 1.15);
  return smoothUnion(0.1, hollow, handle, sphere(0, 1.14, 0, 0.2));
}

/**
 * Snow hut: a thick dome, open underneath, with an entrance niche in front and a chimney
 * whose flue runs from inside out through the roof. Open at both ends, the hut is a ring:
 * the donut's hole becomes the flue. `round` raises the dome's centre above the ground for
 * a fuller, rounder hut.
 */
export function kamakura(round = 0): Sdf {
  const base = -0.4, centre = base + round, top = centre + 1.0;
  const shell = intersect(
    smoothSubtract(0.05, sphere(0, centre, 0, 1.0), sphere(0, centre, 0, 0.62)),
    above(base),
  );
  const chimney = roundedCylinder(0, top + 0.1, 0, 0.27, 0.22, 0.06);
  const flue = roundedCylinder(0, top - 0.1, 0, 0.13, 0.6, 0.02);
  const front = Math.sqrt(1 - (base + 0.25 - centre) ** 2);
  const niche = sphere(0, base + 0.25, front + 0.08, 0.3);
  return smoothSubtract(0.04, smoothUnion(0.12, shell, chimney), union(flue, niche));
}

/**
 * Trousers for a haniwa: puffy legs tied below the knee, hollow from the waist down through
 * both legs, so two holes. `spread` moves the legs apart.
 */
export function trousers(spread = 0): Sdf {
  const legX = 0.44 + spread;
  const hips = roundedCylinder(0, 0.42, 0, legX + 0.3, 0.26, 0.2);
  // Puffy thighs, gathered by a tie (ashiyui) below the knee, over a flared shin.
  const thigh = (s: number) => capsule([s * legX, 0.25, 0], [s * legX, -0.3, 0], 0.36);
  const tie = (s: number): Sdf => (x, y, z) => len2(len2(x - s * legX, z) - 0.26, y + 0.56) - 0.06;
  const shin = (s: number): Sdf => (x, y, z) =>
    roundCone(-0.95, 0.3, -0.62, 0.25)(x - s * legX, y, z);
  const bore = (s: number) => capsule([s * legX, 0.45, 0], [s * legX, -1.5, 0], 0.1);
  return smoothSubtract(
    0.04,
    smoothUnion(0.14, hips, thigh(1), thigh(-1), tie(1), tie(-1), shin(1), shin(-1)),
    union(roundedCylinder(0, 0.9, 0, legX + 0.12, 0.6, 0.1), bore(1), bore(-1)),
  );
}

/**
 * Pastry of a chocolate cornet, lying along x: rolls wound round and round, swelling toward
 * the mouth (+x), and hollow all through. Open at the mouth for the filling and at the tip,
 * it is a ring: the donut's hole becomes the bore. `plump` swells the rolls, which cuts the
 * grooves between them deeper.
 */
export function cornet(plump = 1): Sdf {
  const tip = -1.2, mouth = 1.0, rolls = 6, pitch = (mouth - tip) / (rolls - 1);
  const coils = Array.from({ length: rolls }, (_, i): Sdf => {
    const t = i / (rolls - 1), x0 = tip + pitch * i;
    // Each roll is flattened along the horn, as dough wound on overlapping itself.
    const thick = 0.15 + 0.07 * t, radius = 0.32 + 0.34 * t - thick;
    const across = thick * plump, along = pitch * 0.6;
    return (x, y, z) => len2(len2(y, z) - radius, (x - x0) * across / along) - across;
  });
  // The bore widens from the tip to the mouth; at the tip it keeps a round opening.
  const slope = (0.42 - 0.12) / (mouth - tip);
  const bore = union(
    (x, y, z) => roundCone(tip, 0.12, mouth + 1, 0.42 + slope)(y, x, z),
    capsule([tip, 0, 0], [tip - 1, 0, 0], 0.12),
  );
  return smoothSubtract(0.04, smoothUnion(0.05, ...coils), bore);
}

/**
 * A flat 2D shape (distance `d` in the xy plane) given depth `half` either side of z = 0,
 * its edges rounded by `round`.
 */
const extrude = (d: number, z: number, half: number, round: number) => {
  const a = d + round, b = Math.abs(z) - half + round;
  return Math.min(Math.max(a, b), 0) + len2(Math.max(a, 0), Math.max(b, 0)) - round;
};

/** Distance to the triangle abc in the plane (negative inside). */
function triangle2(
  x: number,
  y: number,
  a: [number, number],
  b: [number, number],
  c: [number, number],
): number {
  const corners = [a, b, c];
  let d = Infinity, inside = 0;
  for (let i = 0; i < 3; i++) {
    const [px, py] = corners[i], [qx, qy] = corners[(i + 1) % 3];
    const ex = qx - px, ey = qy - py, wx = x - px, wy = y - py;
    const h = Math.min(1, Math.max(0, (wx * ex + wy * ey) / (ex * ex + ey * ey)));
    d = Math.min(d, len2(wx - ex * h, wy - ey * h));
    inside += Math.sign(ex * wy - ey * wx);
  }
  return Math.abs(inside) === 3 ? -d : d;
}

/**
 * Sunglasses facing the camera: chunky rims round two lenses, wider at the top, joined by a
 * bridge, with temples running back from their outer ends and bending down behind the ears.
 * The lens openings are the two holes. `ears` is how tall a cat's ear stands on each rim,
 * over its outer corner.
 */
export function sunglasses(ears = 0): Sdf {
  const lensX = 0.6, halfW = 0.38, halfH = 0.28, corner = 0.17, rim = 0.16, half = 0.13;
  const outer = lensX + halfW + rim, top = halfH + rim;
  // Each lens, a rounded box a little wider at the top; |x| mirrors the right one.
  const lens = (x: number, y: number) => {
    const qx = Math.abs(Math.abs(x) - lensX) - halfW - 0.06 * y / halfH + corner;
    const qy = Math.abs(y) - halfH + corner;
    return Math.min(Math.max(qx, qy), 0) + len2(Math.max(qx, 0), Math.max(qy, 0)) - corner;
  };
  const rims: Sdf = (x, y, z) => extrude(Math.abs(lens(x, y)) - rim, z, half, 0.05);
  const bridge = capsule([-0.3, 0.1, 0], [0.3, 0.1, 0], 0.09);
  const temple = (s: number) =>
    union(
      capsule([s * (outer - 0.06), 0.12, -0.05], [s * (outer - 0.02), 0.1, -1.0], 0.09),
      capsule([s * (outer - 0.02), 0.1, -1.0], [s * (outer - 0.02), -0.12, -1.2], 0.09),
    );
  const frame = smoothUnion(0.05, rims, bridge, temple(1), temple(-1));
  if (ears <= 0) return frame;
  const tip: [number, number] = [lensX + halfW * 0.7, top + ears];
  const ear: Sdf = (x, y, z) => {
    const d = triangle2(Math.abs(x), y, [lensX, top - 0.1], [outer - 0.04, top - 0.14], tip);
    return extrude(d - 0.04, z, half, 0.05);
  };
  return smoothUnion(0.06, frame, ear);
}

/**
 * A haniwa's face as a mask, laid face up with its brow away from the camera, so that its rim
 * goes round the wheel's axis: a nearly round plate bent back at the sides,
 * a long ridge of a nose running down from between the brows, and three round openings cut
 * through, two eyes and a mouth below them. The openings are the three holes. `mouth` draws
 * the mouth out up and down into a tall oval.
 */
export function haniwaMask(mouth = 0): Sdf {
  const ellipse = (x: number, y: number, cx: number, cy: number, rx: number, ry: number) =>
    (len2((x - cx) / rx, (y - cy) / ry) - 1) * Math.min(rx, ry);
  const plate: Sdf = (x, y, z) =>
    extrude(ellipse(x, y, 0, 0, 0.9, 0.98), z + 0.28 * x * x, 0.14, 0.07);
  const nose = capsule([0, 0.5, 0.08], [0, -0.16, 0.18], 0.09);
  const openings: Sdf = (x, y, z) =>
    extrude(
      Math.min(
        len2(Math.abs(x) - 0.34, y - 0.2) - 0.14,
        ellipse(x, y, 0, -0.52, 0.14, 0.14 + mouth),
      ),
      z,
      1,
      0,
    );
  // Made facing the camera, then laid down as the starting clay is (see createClayMesh).
  const face = smoothSubtract(0.04, smoothUnion(0.08, plate, nose), openings);
  return (x, y, z) => face(x, -z, y);
}

/** Distance to a rectangle of half sizes hx, hy round (cx, cy), its corners rounded by r. */
function box2(
  x: number,
  y: number,
  cx: number,
  cy: number,
  hx: number,
  hy: number,
  r: number,
): number {
  const qx = Math.abs(x - cx) - hx + r, qy = Math.abs(y - cy) - hy + r;
  return Math.min(Math.max(qx, qy), 0) + len2(Math.max(qx, 0), Math.max(qy, 0)) - r;
}

/** Box of half sizes hx, hy, hz round (cx, cy, cz), its edges rounded by r. */
const roundBox = (
  cx: number,
  cy: number,
  cz: number,
  hx: number,
  hy: number,
  hz: number,
  r: number,
): Sdf =>
(x, y, z) => {
  const qx = Math.abs(x - cx) - hx + r, qy = Math.abs(y - cy) - hy + r;
  const qz = Math.abs(z - cz) - hz + r;
  return Math.min(Math.max(qx, qy, qz), 0) +
    Math.hypot(Math.max(qx, 0), Math.max(qy, 0), Math.max(qz, 0)) - r;
};

/**
 * A peanut's outline in the plane: two rounds of `radius` side by side along x, run
 * together. A smaller `blend` leaves a deeper waist between them.
 */
function peanut2(x: number, y: number, radius: number, blend: number): number {
  return smin(len2(x + 0.62, y) - radius, len2(x - 0.62, y) - radius, blend);
}

/**
 * Bean wholesaler's ornament: a peanut's outline drawn as a thick loop round a peanut-shaped
 * opening, standing upright and facing the camera, so one hole goes through it. `waist`
 * pinches the outside in at the middle, the opening kept as it is.
 */
export function beanLoop(waist = 0): Sdf {
  return (x, y, z) => {
    const outline = Math.max(peanut2(x, y, 0.78, 0.7 - waist), -peanut2(x, y, 0.48, 0.8));
    return extrude(outline, z, 0.26, 0.1);
  };
}

/**
 * Mug of straight sides and corners: a box with a box hollowed out of it from the top, and a
 * handle bent into a frame on its right, the handle's opening the one hole. `sharp` (0–1)
 * takes the rounding off the corners of the body and the handle.
 */
export function squareMug(sharp = 0): Sdf {
  const half = 0.62, halfHeight = 0.8, wall = 0.18, floor = 0.22;
  const corner = 0.32 - 0.24 * sharp;
  const body = roundBox(0, 0, 0, half, halfHeight, half, corner);
  const cavityBottom = -halfHeight + floor, cavityTop = halfHeight + 1;
  const cavity = roundBox(
    0,
    (cavityBottom + cavityTop) / 2,
    0,
    half - wall,
    (cavityTop - cavityBottom) / 2,
    half - wall,
    Math.max(0.04, corner - wall),
  );
  // The handle: a frame in the xy plane, its inner corners as sharp as the outer ones.
  const bend = 0.22 - 0.17 * sharp;
  const handle: Sdf = (x, y, z) => {
    const outer = box2(x, y, half + 0.24, 0, 0.36, 0.5, bend);
    const ring = Math.max(
      outer,
      -box2(x, y, half + 0.24, 0, 0.2, 0.34, Math.max(0.02, bend - 0.12)),
    );
    return extrude(Math.max(ring, half - 0.08 - x), z, 0.13, 0.04);
  };
  return smoothUnion(0.04, smoothSubtract(0.03, body, cavity), handle);
}

/**
 * A face-in-hole board in the shape of a peanut, lying along x and facing the camera, with a
 * round face hole in each half, one low and one high: two holes.
 * `waist` pinches the middle in; `stagger` offsets each hole vertically.
 */
export function peanutPanel(waist = 0, stagger = 0.22): Sdf {
  const blend = 0.9 - waist;
  return (x, y, z) => {
    const board = peanut2(x, y, 0.72, blend);
    const faces = Math.min(
      len2(x + 0.62, y - (0.04 - stagger)) - 0.27,
      len2(x - 0.62, y - (0.04 + stagger)) - 0.27,
    );
    return extrude(Math.max(board, -faces), z, 0.13, 0.05);
  };
}

/**
 * A rice-ball mold: a long plate facing the camera with three triangles cut through it, point
 * up, point down, point up (△▽△). `spread` sets them further apart along the plate.
 */
export function onigiriMold(spread = 0): Sdf {
  const step = 0.64 + spread, side = 0.62, h = side * Math.sqrt(3) / 2;
  const tri = (cx: number, up: boolean) => (x: number, y: number) => {
    const s = up ? 1 : -1;
    return triangle2(
      x,
      y,
      [cx - side / 2, -s * h / 2],
      [cx + side / 2, -s * h / 2],
      [cx, s * h / 2],
    ) - 0.03;
  };
  const holes = [tri(-step, true), tri(0, false), tri(step, true)];
  const halfX = step + side / 2 + 0.24, halfY = h / 2 + 0.22;
  return (x, y, z) => {
    const plate = box2(x, y, 0, 0, halfX, halfY, 0.16);
    const cut = Math.min(...holes.map((d) => d(x, y)));
    return extrude(Math.max(plate, -cut), z, 0.16, 0.06);
  };
}

/** Points along a cubic Bézier curve, ends included. */
function bezier(
  a: [number, number],
  b: [number, number],
  c: [number, number],
  d: [number, number],
  n: number,
): [number, number][] {
  return Array.from({ length: n + 1 }, (_, i) => {
    const t = i / n, s = 1 - t;
    return [
      s ** 3 * a[0] + 3 * s * s * t * b[0] + 3 * s * t * t * c[0] + t ** 3 * d[0],
      s ** 3 * a[1] + 3 * s * s * t * b[1] + 3 * s * t * t * c[1] + t ** 3 * d[1],
    ];
  });
}

/** Distance in the plane to a path through `points`. */
function path2(x: number, y: number, points: [number, number][]): number {
  let d = Infinity;
  for (let i = 1; i < points.length; i++) {
    const [px, py] = points[i - 1], [qx, qy] = points[i];
    const ex = qx - px, ey = qy - py, wx = x - px, wy = y - py;
    const h = Math.min(1, Math.max(0, (wx * ex + wy * ey) / (ex * ex + ey * ey)));
    d = Math.min(d, len2(wx - ex * h, wy - ey * h));
  }
  return d;
}

/**
 * A pretzel facing the camera, of a round rope of dough: a belly across the bottom, from
 * whose ends the rope goes up and over into two loops, down to cross in the middle, and on to
 * be pressed onto the belly on the other side. The two loops and the room under the crossing
 * are three holes. `loops` widens the rope's course against its thickness, the loops opening.
 */
export function pretzel(loops = 1): Sdf {
  const w = 0.95, r = 0.15;
  const belly = bezier([-w, 0], [-w, -0.95], [w, -0.95], [w, 0], 24);
  const loop = (s: number) => bezier([s * w, 0], [s * w, 0.95], [s * 0.05, 0.85], [0, -0.08], 24);
  const arm = (s: number): [number, number][] => [[0, -0.08], [-s * 0.5, -0.62]];
  const course = [belly, loop(1), loop(-1), arm(1), arm(-1)];
  return (x, y, z) => {
    const px = x / loops, py = y / loops;
    const d = Math.min(...course.map((points) => path2(px, py, points))) * loops;
    return len2(d, z) - r;
  };
}

/**
 * A decorative plate of a three-pin socket facing the camera: a rounded square plate with two
 * slots slanting in above (ハの字) and one upright below, as the sockets of some countries
 * have. The slots are the three holes. `spread` sets them further apart.
 */
export function outletPlate(spread = 0): Sdf {
  const slot = (cx: number, cy: number, angle: number) => (x: number, y: number) => {
    const c = Math.cos(angle), s = Math.sin(angle);
    const u = (x - cx) * c + (y - cy) * s, v = -(x - cx) * s + (y - cy) * c;
    return box2(u, v, 0, 0, 0.08, 0.2, 0.05);
  };
  const sx = 0.3 + spread, top = 0.22 + spread * 0.6, bottom = -0.32 - spread * 0.6;
  const slots = [slot(-sx, top, -Math.PI / 6), slot(sx, top, Math.PI / 6), slot(0, bottom, 0)];
  return (x, y, z) => {
    const plate = box2(x, y, 0, 0, 0.88, 0.88, 0.28);
    const cut = Math.min(...slots.map((d) => d(x, y)));
    return extrude(Math.max(plate, -cut), z, 0.17, 0.08);
  };
}

/**
 * A wooden window frame of four panes facing the camera: a square frame and a cross of bars
 * dividing it. The panes are the four holes. `slim` thins the frame and the bars, the panes
 * opening wider.
 */
export function windowFrame(slim = 0): Sdf {
  const half = 0.92, border = 0.24 - slim, bar = 0.1 - slim / 3;
  return (x, y, z) => {
    const frame = box2(x, y, 0, 0, half, half, 0.06);
    const pane = (sx: number, sy: number) => {
      const size = (half - border - bar) / 2;
      const c = bar + size;
      return box2(x, y, sx * c, sy * c, size, size, 0.03);
    };
    const panes = Math.min(pane(-1, -1), pane(1, -1), pane(-1, 1), pane(1, 1));
    return extrude(Math.max(frame, -panes), z, 0.2, 0.04);
  };
}

/**
 * A haniwa giraffe on wheels, side on to the camera like a pull toy: a body along x, a long
 * neck and a small head up on the right, and four stubby legs in a row under it, each on a
 * wheel whose opening faces the camera. The four wheels are the four holes. `stance` sets
 * the legs further apart.
 */
export function kirin(stance = 0): Sdf {
  const wheelY = -0.7, major = 0.14;
  const legXs = [-0.9 - stance, -0.34 - stance / 2, 0.3 + stance / 2, 0.86 + stance];
  const body = capsule([-0.72, 0, 0], [0.62, 0.04, 0], 0.3);
  const neck = capsule([0.5, 0.1, 0], [0.8, 0.95, 0], 0.15);
  const head = capsule([0.74, 1.0, 0], [1.06, 0.9, 0], 0.13);
  const horns = union(
    capsule([0.78, 1.06, 0.11], [0.74, 1.24, 0.13], 0.045),
    capsule([0.78, 1.06, -0.11], [0.74, 1.24, -0.13], 0.045),
  );
  const tail = capsule([-0.98, 0.06, 0], [-1.1, -0.16, 0], 0.06);
  const legs = legXs.map((x) => capsule([x * 0.92, -0.1, 0], [x, wheelY + major + 0.02, 0], 0.1));
  const wheels = legXs.map((x) => torusZ(x, wheelY, 0, major, 0.075));
  return smoothUnion(0.06, smoothUnion(0.1, body, neck, head, horns, tail), ...legs, ...wheels);
}

/**
 * A big four-holed button facing the camera: a thick disc with a raised lip round a dished
 * middle, and four holes in a square through it. `spread` sets the holes further apart.
 */
export function bigButton(spread = 0): Sdf {
  const at = 0.24 + spread, radius = 0.11;
  return (x, y, z) => {
    const r = len2(x, y);
    // The face dips toward the middle, inside the lip.
    const dish = 0.04 * Math.max(0, 1 - ((r - 0.98) / 0.3) ** 2) - 0.05 * Math.max(0, 1 - r / 0.75);
    const disc = extrude(r - 0.98, z, 0.17 + dish, 0.08);
    const holes = Math.min(
      len2(x - at, y - at),
      len2(x + at, y - at),
      len2(x - at, y + at),
      len2(x + at, y + at),
    ) - radius;
    return Math.max(disc, -extrude(holes, z, 1, 0));
  };
}

export const TARGET_DEFS: TargetDef[] = [
  {
    id: "jomonyan",
    item: "ねこ頭形土製品",
    praise: "二つのお耳がぴんと立った、愛らしいねこの土製品です",
    clay: "blob",
    feature: "ears",
    par: 20,
    sdf: catHead(),
    bound: 1.35,
  },
  {
    id: "gourd",
    item: "ひょうたん",
    praise: "くびれが見事なひょうたんです",
    clay: "blob",
    feature: "waist",
    par: 20,
    sdf: gourd(),
    bound: 1.6,
  },
  {
    id: "snowman",
    item: "雪だるま",
    praise: "頭と胴のつりあいがかわいいです",
    clay: "blob",
    feature: "waist",
    par: 20,
    sdf: snowman,
    bound: 1.7,
  },
  {
    id: "peanut",
    item: "ピーナッツ",
    praise: "くびれがいかにもピーナッツです",
    clay: "blob",
    feature: "waist",
    par: 20,
    sdf: peanut,
    bound: 1.6,
  },
  {
    id: "mug",
    item: "マグカップ",
    praise: "取っ手が立派でよい品です",
    clay: "donut",
    feature: "hole",
    par: 50,
    sdf: mug(),
    bound: 1.6,
  },
  {
    id: "wateringCan",
    item: "じょうろ",
    praise: "持ち手がにぎりやすい、よいじょうろです",
    clay: "donut",
    feature: "hole",
    par: 50,
    sdf: wateringCan(),
    bound: 1.4,
  },
  {
    id: "bell",
    item: "福引の鐘",
    praise: "よく鳴りそうな、よい鐘です",
    clay: "blob",
    feature: "waist",
    par: 30,
    sdf: bell(),
    bound: 1.5,
  },
  {
    id: "kamakura",
    item: "かまくら",
    praise: "煙突がちゃんと抜けていて、あたたかそうです",
    clay: "donut",
    clayHoles: "up",
    feature: "hole",
    par: 50,
    sdf: kamakura(),
    bound: 1.2,
  },
  {
    id: "cornet",
    item: "コロネ",
    praise: "奥までしっかり空いていて、チョコがたっぷり入りそうです",
    clay: "donut",
    clayHoles: "side",
    feature: "hole",
    par: 60,
    sdf: cornet(),
    bound: 1.6,
  },
  {
    id: "trousers",
    item: "はにわのおズボン",
    praise: "脚の通りがよく、はにわも喜びそうです",
    clay: "pretzel",
    clayHoles: "up",
    feature: "hole",
    par: 60,
    sdf: trousers(),
    bound: 1.5,
  },
  {
    id: "sunglasses",
    item: "サングラス",
    praise: "レンズがぴったりはまりそうな、よいフレームです",
    clay: "pretzel",
    feature: "hole",
    par: 60,
    sdf: sunglasses(),
    bound: 1.5,
  },
  {
    id: "haniwaMask",
    item: "はにわのお面",
    praise: "目と口がきれいに抜けた、素朴なよいお面です",
    clay: "trefoil",
    clayHoles: "up",
    thumbView: [0, 1, 0.3],
    feature: "hole",
    par: 70,
    sdf: haniwaMask(),
    bound: 1.3,
  },
  {
    id: "beanLoop",
    item: "豆のオブジェ",
    praise: "まん中のくびれが、どこから見てもお豆さんです",
    clay: "donut",
    feature: "hole",
    par: 50,
    sdf: beanLoop(),
    bound: 1.6,
  },
  {
    id: "squareMug",
    item: "角張りマグ",
    praise: "角がきりっと立った、未来的なマグです",
    clay: "donut",
    feature: "hole",
    par: 60,
    sdf: squareMug(),
    bound: 1.4,
  },
  {
    id: "peanutPanel",
    item: "顔はめパネル",
    praise: "顔穴がちょうどよい所にあいていて、並んで撮れそうです",
    clay: "pretzel",
    feature: "hole",
    par: 60,
    sdf: peanutPanel(),
    bound: 1.5,
  },
  {
    id: "onigiriMold",
    item: "おにぎりの型",
    praise: "三角の穴がそろっていて、きっちり握れそうです",
    clay: "trefoil",
    feature: "hole",
    par: 70,
    sdf: onigiriMold(),
    bound: 1.5,
  },
  {
    id: "pretzel",
    item: "プレッツェル",
    praise: "交差もふたつの輪もきれいで、いかにもプレッツェルです",
    clay: "trefoil",
    feature: "hole",
    par: 70,
    sdf: pretzel(),
    bound: 1.4,
  },
  {
    id: "outletPlate",
    item: "コンセントの飾り板",
    praise: "三つの穴の並びがきれいで、ひと目でコンセントと分かります",
    clay: "trefoil",
    feature: "hole",
    par: 70,
    sdf: outletPlate(),
    bound: 1.2,
  },
  {
    id: "windowFrame",
    item: "四つ窓枠",
    praise: "四つの窓がそろって、桟もまっすぐ通っています",
    clay: "quatrefoil",
    feature: "hole",
    par: 80,
    sdf: windowFrame(),
    bound: 1.2,
  },
  {
    id: "kirin",
    item: "四駆のキリン",
    praise: "四つの車輪がしっかり回りそうな、頼もしいキリンです",
    clay: "quatrefoil",
    feature: "hole",
    par: 80,
    sdf: kirin(),
    bound: 1.4,
  },
  {
    id: "bigButton",
    item: "四つ穴ボタン",
    praise: "四つの穴が整っていて、立派なボタンです",
    clay: "quatrefoil",
    feature: "hole",
    par: 80,
    sdf: bigButton(),
    bound: 1.3,
  },
];

/** Cell size of the displayed and scored target mesh, in unscaled units. */
const MESH_CELL = 0.05;
/** Vertex spacing of the reference mesh: about that of the clay. */
const REFERENCE_SPACING = 0.03;

/**
 * A target meshed, scaled and centred (see Target.mesh): plain data, so that it can be made
 * in a worker (see mesher.ts) and passed out of it.
 */
export interface TargetMesh extends IndexedMesh {
  normals: Float32Array;
  areas: Float32Array;
  /** What the unscaled shape is scaled by, then moved by, to be placed. */
  scale: number;
  shift: [number, number, number];
}

/** Places unscaled positions as the target's are (see TargetMesh). */
function place(p: Float32Array, scale: number, shift: THREE.Vector3): void {
  for (let i = 0; i < p.length; i += 3) {
    p[i] = p[i] * scale + shift.x;
    p[i + 1] = p[i + 1] * scale + shift.y;
    p[i + 2] = p[i + 2] * scale + shift.z;
  }
}

export class Target {
  readonly def: TargetDef;
  readonly positions: Float32Array;
  readonly index: Uint32Array;
  readonly normals: Float32Array;
  readonly areas: Float32Array;
  readonly scale: number;
  readonly shift: THREE.Vector3;

  /**
   * Meshes the target scaled to `volume` with its volume centroid at the origin. It takes
   * a while, so the stages have it done in a worker.
   */
  static mesh(def: TargetDef, volume: number): TargetMesh {
    const { positions, index } = meshSdf(def.sdf, def.bound, MESH_CELL);
    const n = positions.length / 3;
    const normals = new Float32Array(n * 3), areas = new Float32Array(n);
    const raw = computeStats(positions, index, normals, areas);
    const scale = Math.cbrt(volume / raw.volume);
    const shift = raw.centroid.clone().multiplyScalar(-scale);
    place(positions, scale, shift);
    computeStats(positions, index, normals, areas);
    return { positions, index, normals, areas, scale, shift: shift.toArray() };
  }

  /** The target of `def`, as `mesh` (made by Target.mesh) has it. */
  constructor(def: TargetDef, mesh: TargetMesh) {
    this.def = def;
    this.positions = mesh.positions;
    this.index = mesh.index;
    this.normals = mesh.normals;
    this.areas = mesh.areas;
    this.scale = mesh.scale;
    this.shift = new THREE.Vector3(...mesh.shift);
  }

  /**
   * The same surface meshed at about the clay's vertex spacing: a perfect answer as the
   * clay could represent it. It takes longer than the target's own mesh.
   */
  referenceMesh(): IndexedMesh {
    const mesh = meshSdf(this.def.sdf, this.def.bound, REFERENCE_SPACING / this.scale);
    place(mesh.positions, this.scale, this.shift);
    return mesh;
  }

  /** Axis (0 = x, 1 = y, 2 = z) along which the target is longest. */
  get mainAxis(): number {
    const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < this.positions.length; i++) {
      min[i % 3] = Math.min(min[i % 3], this.positions[i]);
      max[i % 3] = Math.max(max[i % 3], this.positions[i]);
    }
    const extent = [0, 1, 2].map((a) => max[a] - min[a]);
    return extent.indexOf(Math.max(...extent));
  }

  /** Approximate signed distance to the (scaled, centred) target surface. */
  sdf(x: number, y: number, z: number): number {
    const s = this.scale;
    return this.def.sdf((x - this.shift.x) / s, (y - this.shift.y) / s, (z - this.shift.z) / s) * s;
  }

  createGeometry(): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(new THREE.BufferAttribute(this.index, 1));
    geometry.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    geometry.setAttribute("normal", new THREE.BufferAttribute(this.normals, 3));
    geometry.computeBoundingSphere();
    return geometry;
  }
}
